import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSubjectQuizScores } from '../utils/quizService';

/**
 * Custom hook for managing subject quiz scores
 * Provides real-time subject ranking data with refresh capabilities
 */
export const useSubjectScores = () => {
  const [subjectData, setSubjectData] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [error, setError] = useState(null);

  // Default subject data (fallback) - memoized to prevent recreating on every render
  const defaultSubjectData = useMemo(() => [
    { subject: 'Science', score: 0, color: '#4a148c' },
    { subject: 'History', score: 0, color: '#6a1b9a' },
    { subject: 'Math', score: 0, color: '#2e7d32' },
    { subject: 'English', score: 0, color: '#1565c0' },
    { subject: 'Art', score: 0, color: '#e65100' },
  ], []);

  // Fetch subject scores from backend
  const fetchSubjectScores = useCallback(async () => {
    try {
      console.log('📊 Fetching subject quiz scores...');
      setLoadingSubjects(true);
      setError(null);
      
      const data = await getSubjectQuizScores();
      
      console.log('🔍 Raw data from API:', JSON.stringify(data, null, 2));
      console.log('🔍 Data type:', typeof data);
      console.log('🔍 Data keys:', data ? Object.keys(data) : 'null');
      
      if (data && data.subject_ranking && data.subject_ranking.length > 0) {
        // Transform the data for the chart
        const transformedData = data.subject_ranking.map((subject, index) => {
          console.log(`📊 Processing subject: ${subject.subject}`);
          console.log(`📊 Original average_score: ${subject.average_score}`);
          console.log(`📊 Original total_score: ${subject.total_score}`);
          console.log(`📊 Original total_quizzes: ${subject.total_quizzes}`);
          console.log(`📊 Original accuracy: ${subject.accuracy}`);
          
          const transformed = {
            subject: subject.subject,
            score: Math.round(subject.average_score * 10), // Convert to 0-100 scale
            color: defaultSubjectData[index % defaultSubjectData.length]?.color || '#666666',
            total_score: subject.total_score,
            total_quizzes: subject.total_quizzes,
            accuracy: Math.round(subject.accuracy)
          };
          
          console.log(`📊 Transformed score: ${transformed.score}`);
          return transformed;
        });
        
        setSubjectData(transformedData);
        console.log('✅ Subject scores loaded and transformed:', transformedData);
        console.log('🔄 Setting loading to false after successful data load');
      } else {
        console.log('📊 No quiz data found in response, using default data');
        console.log('📊 Data structure:', { 
          hasData: !!data, 
          hasRanking: data?.subject_ranking !== undefined,
          rankingLength: data?.subject_ranking?.length 
        });
        setSubjectData(defaultSubjectData);
        console.log('🔄 Setting loading to false after using default data');
      }
    } catch (error) {
      console.error('❌ Error fetching subject scores:', error);
      setError(error.message);
      setSubjectData(defaultSubjectData);
      console.log('🔄 Setting loading to false after error');
    } finally {
      console.log('🔄 Finally block: Setting loading to false');
      setLoadingSubjects(false);
    }
  }, [defaultSubjectData]);

  // Refresh function that can be called from outside
  const refreshSubjectScores = useCallback(() => {
    console.log('🔄 Refreshing subject scores...');
    fetchSubjectScores();
  }, [fetchSubjectScores]);

  // Initial fetch on mount
  useEffect(() => {
    fetchSubjectScores();
  }, [fetchSubjectScores]);

  return {
    subjectData,
    loadingSubjects,
    error,
    refreshSubjectScores
  };
};
